import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useApolloClient } from '@apollo/client/react';
import { useMaturityFramework } from '../../../graphql/queries';
import { useAppSelector } from '../../../store';
import { selectUser } from '../../../store/features/auth/authSlice';
import { hasPermission } from '../../../lib/permissions';
import { MaturityFrameworkForm } from '../../../components/maturity';
import { updateMaturityFramework, ApiError } from '../../../lib/apiClient';
import { GET_MATURITY_FRAMEWORK, LIST_MATURITY_FRAMEWORKS } from '../../../graphql/queries';
import type { CreateMaturityFrameworkFormData } from '../../../components/forms/validation';
import { DetailSkeleton, EmptyState } from '../../../components/shared';
import '@tylertech/forge/banner';
import './$frameworkId.edit.css';

export const Route = createFileRoute('/maturity/frameworks/$frameworkId/edit')({
  component: EditMaturityFrameworkPage,
});

export { EditMaturityFrameworkPage };

function EditMaturityFrameworkPage(): JSX.Element {
  const { frameworkId } = Route.useParams();
  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  const user = useAppSelector(selectUser);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data, loading, error: fetchError } = useMaturityFramework(frameworkId);
  const framework = data?.maturityFramework;

  const BannerElement = 'forge-banner' as React.ElementType;

  if (!hasPermission(user, 'maturityFramework:update')) {
    void navigate({ to: '/maturity/frameworks/$frameworkId', params: { frameworkId } });
    return <></>;
  }

  if (loading)
    return (
      <div className="edit-framework-page">
        <DetailSkeleton cards={1} />
      </div>
    );

  if (fetchError || !framework) {
    return (
      <div className="edit-framework-page">
        <EmptyState
          icon="error"
          title="Failed to load framework"
          description={fetchError?.message ?? 'Framework not found'}
          actionLabel="Back to Frameworks"
          onAction={() => void navigate({ to: '/maturity/frameworks' })}
        />
      </div>
    );
  }

  const handleSubmit = async (data: CreateMaturityFrameworkFormData): Promise<void> => {
    setIsSubmitting(true);
    setError(null);

    try {
      await updateMaturityFramework(frameworkId, {
        name: data.name,
        description: data.description,
        version: data.version,
        status: data.status,
        effectiveFrom: data.effectiveFrom,
        effectiveTo: data.effectiveTo,
      });

      await apolloClient.refetchQueries({
        include: [GET_MATURITY_FRAMEWORK, LIST_MATURITY_FRAMEWORKS],
      });

      void navigate({
        to: '/maturity/frameworks/$frameworkId',
        params: { frameworkId },
      });
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to update framework';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = (): void => {
    void navigate({ to: '/maturity/frameworks/$frameworkId', params: { frameworkId } });
  };

  return (
    <div className="edit-framework-page">
      <div className="page-header page-header--toolbar page-header--center">
        <h1 className="forge-typography--headline5">Edit Framework: {framework.name}</h1>
      </div>

      {error && (
        <BannerElement theme="danger">
          <span>{error}</span>
        </BannerElement>
      )}

      <MaturityFrameworkForm
        mode="edit"
        defaultValues={{
          name: framework.name,
          description: framework.description ?? undefined,
          version: framework.version,
          status: framework.status,
          effectiveFrom: framework.effectiveFrom.split('T')[0],
          effectiveTo: framework.effectiveTo?.split('T')[0] ?? undefined,
        }}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isSubmitting={isSubmitting}
      />
    </div>
  );
}
